import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id?: string;
  email: string;
  name: string;
  avatar?: string;
  provider?: 'local' | 'google';
  role?: string; // Added for admin differentiation
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (userData: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Client-side fast path: a token whose exp is in the past can never be valid,
// so drop it without a network round-trip. Unparseable tokens are left to the
// server-side /api/auth/me check.
function isJwtExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

interface AuthProviderProps {
  children: ReactNode;
  storagePrefix?: string; // Allow different storage keys for different apps
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  storagePrefix = 'cleveroo' 
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const tokenKey = `${storagePrefix}_token`;
  const userKey = `${storagePrefix}_user`;

  // Check authentication info in localStorage on initialization.
  // A stored token is NOT trusted blindly: it must not be expired (local exp
  // check) and must pass /api/auth/me (server check). Until that resolves,
  // isLoading stays true so the authenticated shell is never flashed and then
  // yanked away. Network failures keep the session — being offline must not
  // log the user out; only a definitive 401/403 does.
  useEffect(() => {
    let cancelled = false;

    const clearStoredAuth = () => {
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(userKey);
    };

    const initAuth = async () => {
      let savedToken: string | null = null;
      let savedUser: User | null = null;
      try {
        savedToken = localStorage.getItem(tokenKey);
        const rawUser = localStorage.getItem(userKey);
        savedUser = rawUser ? JSON.parse(rawUser) : null;
      } catch (error) {
        console.error('Failed to initialize authentication state:', error);
        clearStoredAuth();
      }

      if (!savedToken || !savedUser) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      if (isJwtExpired(savedToken)) {
        clearStoredAuth();
        if (!cancelled) setIsLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` },
        });
        if (res.status === 401 || res.status === 403) {
          clearStoredAuth();
          if (!cancelled) setIsLoading(false);
          return;
        }
      } catch {
        // Network error — fall through and keep the stored session.
      }

      if (!cancelled) {
        setToken(savedToken);
        setUser(savedUser);
        setIsLoading(false);
      }
    };

    initAuth();
    return () => { cancelled = true; };
  }, [tokenKey, userKey]);

  const login = (userData: User, userToken: string) => {
    console.log('🔐 AuthContext login called with:', { userData, userToken });
    
    setUser(userData);
    setToken(userToken);
    
    // Save to localStorage
    localStorage.setItem(tokenKey, userToken);
    localStorage.setItem(userKey, JSON.stringify(userData));
    
    console.log('💾 AuthContext: User and token stored');
    console.log('✅ AuthContext: Authentication should now be true');
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    
    // Clear localStorage
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(userKey);
    
    // Also clear the generic access/refresh tokens if they exist
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem(userKey, JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;